import React, { FC, useState } from "react";
import { Copy, Download, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Reservation } from "@/types/reservation";
import { Document, DocumentType } from "@/types/document";
import { FoldableContainer } from "@/components/pages/chats/component/booking-info-components/FoldableContainer";
import { PresignedImage } from "@/components/common/PresignedImage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fileService } from "@/services/file-service";
import { copyToClipboard } from "@/utils/clipboard";

interface DocumentsBookingInfoProps {
  booking: Reservation;
}

const openDocument = async (fileId: string) => {
  const url = await fileService.getSignedUrl(fileId);
  if (url) {
    window.open(url, "_blank", "noopener,noreferrer");
  }
};

interface DocumentActionsProps {
  fileId: string;
  documentType: DocumentType;
}

const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const DocumentActions: FC<DocumentActionsProps> = ({
  fileId,
  documentType,
}) => {
  const { t } = useTranslation();
  const [isCopying, setIsCopying] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const filename = `document-${documentType.toLowerCase()}`;

  const handleCopy = async () => {
    try {
      setIsCopying(true);
      const signedUrl = await fileService.getSignedUrl(fileId);
      const copied = await copyToClipboard(signedUrl);

      if (!copied) {
        throw new Error("Could not copy document link");
      }

      toast.success(t("chats.documentLinkCopied"));
    } catch {
      toast.error(t("chats.documentCopyError"));
    } finally {
      setIsCopying(false);
    }
  };

  const handleDownload = async () => {
    let signedUrl: string | undefined;

    try {
      setIsDownloading(true);
      signedUrl = await fileService.getSignedUrl(fileId);
      const response = await fetch(signedUrl);

      if (!response.ok) {
        throw new Error("Could not download document");
      }

      downloadBlob(await response.blob(), filename);
      toast.success(t("chats.documentDownloaded"));
    } catch {
      if (signedUrl) {
        window.open(signedUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.error(t("chats.documentDownloadError"));
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <Button
        type="button"
        variant="new_ghost"
        size="icon_sm"
        onClick={handleCopy}
        disabled={isCopying || isDownloading}
        aria-label={t("common.copy")}
      >
        {isCopying ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Copy className="size-4" />
        )}
      </Button>
      <Button
        type="button"
        variant="new_ghost"
        size="icon_sm"
        onClick={handleDownload}
        disabled={isCopying || isDownloading}
        aria-label={t("chats.downloadDocument")}
      >
        {isDownloading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Download className="size-4" />
        )}
      </Button>
    </div>
  );
};

interface DocumentGroupProps {
  label: string;
  documents: Document[];
}

const DocumentGroup: FC<DocumentGroupProps> = ({ label, documents }) => {
  if (!documents.length) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span className="text-xs text-neutral-500">{label}</span>
        <DocumentActions
          fileId={documents[0].fileId}
          documentType={documents[0].type ?? DocumentType.OTHER}
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {documents.map((doc) => (
          <button
            key={doc.id}
            type="button"
            onClick={() => openDocument(doc.fileId)}
            className="cursor-pointer overflow-hidden rounded-lg border border-neutral-200"
          >
            <PresignedImage
              fileId={doc.fileId}
              alt={`${label} document`}
              className="h-[135px] w-[90px] object-cover"
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export const DocumentsBookingInfo: FC<DocumentsBookingInfoProps> = ({
  booking,
}) => {
  const { t } = useTranslation();
  const documents = booking?.guest?.documents ?? [];

  if (!documents.length) {
    return null;
  }

  // App convention (see CheckinBookingInfo): non-OTHER docs are IDs/passports,
  // OTHER docs are the signed contract.
  const idDocuments = documents.filter(
    (doc) => doc.type !== DocumentType.OTHER,
  );
  const contractDocuments = documents.filter(
    (doc) => doc.type === DocumentType.OTHER,
  );

  return (
    <FoldableContainer
      titleNode={t("chats.documents")}
      extraNode={
        <Badge variant="outline" className="shrink-0">
          {documents.length}
        </Badge>
      }
    >
      <div className="flex flex-col gap-4">
        <DocumentGroup label={t("chats.passport")} documents={idDocuments} />
        <DocumentGroup
          label={t("common.contract")}
          documents={contractDocuments}
        />
      </div>
    </FoldableContainer>
  );
};
